import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FaArrowLeft, FaCalendarAlt, FaCrown, FaUsers, FaUserFriends, FaRegClock, FaMapMarkerAlt, FaImage, FaInfoCircle, FaThLarge, FaList, FaEdit, FaCheck, FaTimes, FaUserCheck, FaPaperPlane, FaInbox, FaSearch, FaSpinner } from 'react-icons/fa';
import { MdEventSeat, MdVerified, MdOutlineAccessTime, MdLock, MdPublic } from 'react-icons/md';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import EditEventDateModal from '../Modals/EditEventDateModal';
import EventdescView from '../Modals/Eventdescription';
import EventImageView from '../Modals/EventImageView';
import AddressView from '../Modals/Addressview';
import { GetEvents, PlansList, AssignUserPlan, UnblockGrouplists, GetUserFollowDetails, AcceptUserFollowRequest, RejectUserFollowRequest } from '../api/api';
import { DecryptData } from '../config/common';
import Config from '../config/config';
import DefultImage from '../assets/images/emptyprofile.png';

function UserDetails() {
    const location = useLocation();
    const navigate = useNavigate();
    const userFromState = location.state;

    const [user, setUser] = useState(userFromState || null);
    const [activeTab, setActiveTab] = useState('events'); // 'events', 'groups', 'premium', 'following'

    // Events State
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventSearch, setEventSearch] = useState('');
    const [eventFilter, setEventFilter] = useState('all'); // 'all', 'hosted', 'joined', 'started', 'comingsoon', 'finished'
    const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

    // Groups State
    const [groups, setGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupSearch, setGroupSearch] = useState('');
    const [groupFilter, setGroupFilter] = useState('all'); // 'all', 'created', 'admin', 'member'
    const [groupViewMode, setGroupViewMode] = useState('cards'); // 'cards' | 'table'

    // Event Modals State
    const [selectedEventForEditDate, setSelectedEventForEditDate] = useState(null);
    const [showEditDateModal, setShowEditDateModal] = useState(false);
    const [selectedEventDesc, setSelectedEventDesc] = useState(null);
    const [showDescModal, setShowDescModal] = useState(false);
    const [selectedEventImages, setSelectedEventImages] = useState(null);
    const [showImagesModal, setShowImagesModal] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [showAddressModal, setShowAddressModal] = useState(false);

    // Premium Plans State
    const [availablePlans, setAvailablePlans] = useState([]);
    const [premiumSubTab, setPremiumSubTab] = useState('extend'); // 'extend' | 'assign'
    const [extendDays, setExtendDays] = useState(30);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [customPlanType, setCustomPlanType] = useState('Monthly Premium');
    const [customPlanDays, setCustomPlanDays] = useState(30);
    const [customPlanPrice, setCustomPlanPrice] = useState(0);
    const [planLoading, setPlanLoading] = useState(false);

    // Follow & Connections State
    const [followDetails, setFollowDetails] = useState({
        counts: { pendingReceived: 0, pendingSent: 0, followers: 0, following: 0 },
        requestsReceived: [],
        requestsSent: [],
        followers: [],
        following: []
    });
    const [followLoading, setFollowLoading] = useState(false);
    const [followSubTab, setFollowSubTab] = useState('received'); // 'received' | 'sent' | 'followers' | 'following'
    const [followSearch, setFollowSearch] = useState('');
    const [followActionLoading, setFollowActionLoading] = useState({});

    // Load available admin plans
    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const resp = await PlansList();
                const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;
                if (data?.status && Array.isArray(data?.data)) {
                    setAvailablePlans(data.data);
                }
            } catch (err) {
                console.error('Error fetching plans:', err);
            }
        };
        fetchPlans();
    }, []);

    // Load User Events
    const fetchUserEvents = async (uid) => {
        if (!uid) return;
        try {
            setEventsLoading(true);
            const resp = await GetEvents({ userId: uid });
            const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;
            if (data?.status && Array.isArray(data?.data)) {
                setEvents(data.data);
            } else {
                setEvents([]);
            }
        } catch (err) {
            console.error('Error fetching user events:', err);
            setEvents([]);
        } finally {
            setEventsLoading(false);
        }
    };

    // Load User Groups
    const fetchUserGroups = async (uid) => {
        if (!uid) return;
        try {
            setGroupsLoading(true);
            const resp = await UnblockGrouplists({ userId: uid });
            const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;
            if (data?.status && Array.isArray(data?.data)) {
                setGroups(data.data);
            } else {
                setGroups([]);
            }
        } catch (err) {
            console.error('Error fetching user groups:', err);
            setGroups([]);
        } finally {
            setGroupsLoading(false);
        }
    };

    // Fetch Follow & Connections Details
    const fetchFollowDetails = async (uid) => {
        if (!uid) return;
        try {
            setFollowLoading(true);
            const resp = await GetUserFollowDetails({ userId: uid });
            const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;
            if (data?.status && data?.data) {
                setFollowDetails(data.data);
                if (data.data.counts) {
                    setUser((prev) => ({
                        ...prev,
                        followersCount: data.data.counts.followers,
                        followingCount: data.data.counts.following
                    }));
                }
            }
        } catch (err) {
            console.error('Error fetching follow details:', err);
        } finally {
            setFollowLoading(false);
        }
    };

    // Follow Request Action Handlers
    const handleAcceptFollowRequest = async (requestId) => {
        if (!requestId) return;
        try {
            setFollowActionLoading((prev) => ({ ...prev, [requestId]: 'accepting' }));
            const resp = await AcceptUserFollowRequest({ requestId });
            console.log('AcceptUserFollowRequest resp:', resp);
            const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;
            console.log('AcceptUserFollowRequest data:', data);
            if (data?.status) {
                toast.success(data?.message || data?.msg || 'Follow request accepted successfully');
                if (user?._id) {
                    fetchFollowDetails(user._id);
                }
            } else {
                const errorMsg = data?.message || data?.msg || (typeof resp === 'string' ? resp : 'Failed to accept request');
                toast.error(errorMsg);
            }
        } catch (err) {
            console.error('Error accepting follow request:', err);
            toast.error(err?.message || 'Failed to accept follow request');
        } finally {
            setFollowActionLoading((prev) => ({ ...prev, [requestId]: false }));
        }
    };

    const handleRejectFollowRequest = async (requestId) => {
        if (!requestId) return;
        if (!window.confirm('Are you sure you want to remove this follow request?')) return;
        try {
            setFollowActionLoading((prev) => ({ ...prev, [requestId]: 'rejecting' }));
            const resp = await RejectUserFollowRequest({ requestId });
            console.log('RejectUserFollowRequest resp:', resp);
            const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;
            console.log('RejectUserFollowRequest data:', data);
            if (data?.status) {
                toast.success(data?.message || data?.msg || 'Follow request removed successfully');
                if (user?._id) {
                    fetchFollowDetails(user._id);
                }
            } else {
                const errorMsg = data?.message || data?.msg || (typeof resp === 'string' ? resp : 'Failed to remove request');
                toast.error(errorMsg);
            }
        } catch (err) {
            console.error('Error rejecting follow request:', err);
            toast.error(err?.message || 'Failed to remove follow request');
        } finally {
            setFollowActionLoading((prev) => ({ ...prev, [requestId]: false }));
        }
    };

    useEffect(() => {
        if (user?._id) {
            fetchUserEvents(user._id);
            fetchUserGroups(user._id);
            fetchFollowDetails(user._id);
        }
    }, [user?._id]);

    // Determine current active plan from user.plan
    const getActivePlan = () => {
        if (!user?.plan) return null;
        if (Array.isArray(user.plan)) {
            const active = user.plan.find(p => p && p.isActive !== 'expired');
            return active || (user.plan.length > 0 ? user.plan[user.plan.length - 1] : null);
        }
        if (typeof user.plan === 'object' && user.plan._id) {
            return user.plan;
        }
        return null;
    };

    const currentPlan = getActivePlan();
    const isPremiumActive = currentPlan && (currentPlan.isActive === 'started' || (currentPlan.EndDate && new Date(currentPlan.EndDate) > new Date()));

    // Filtered Events
    const filteredEvents = events.filter((ev) => {
        // Search filter
        if (eventSearch.trim()) {
            const q = eventSearch.toLowerCase();
            const titleMatch = ev.eventTitle?.toLowerCase().includes(q);
            const addrMatch = ev.eventAddress?.toLowerCase().includes(q);
            const descMatch = ev.description?.toLowerCase().includes(q);
            if (!titleMatch && !addrMatch && !descMatch) return false;
        }

        // Category / Status Filter
        const isHost = (ev.userId?._id || ev.userId) === user?._id;
        if (eventFilter === 'hosted') return isHost;
        if (eventFilter === 'joined') return !isHost;
        if (eventFilter === 'started') return ev.event_status === 'started';
        if (eventFilter === 'comingsoon') return ev.event_status === 'comingsoon';
        if (eventFilter === 'finished') return ev.event_status === 'finished';

        return true;
    });

    // Filtered Groups
    const filteredGroups = groups.filter((grp) => {
        if (groupSearch.trim()) {
            const q = groupSearch.toLowerCase();
            const nameMatch = grp.chatName?.toLowerCase().includes(q);
            const addrMatch = grp.groupAddress?.toLowerCase().includes(q);
            const descMatch = grp.groupDiscription?.toLowerCase().includes(q);
            if (!nameMatch && !addrMatch && !descMatch) return false;
        }

        const isCreator = (grp.groupCreater?._id || grp.groupCreater) === user?._id;
        const isAdmin = Array.isArray(grp.groupAdmin) && grp.groupAdmin.some(a => (a?._id || a) === user?._id);

        if (groupFilter === 'created') return isCreator;
        if (groupFilter === 'admin') return isAdmin && !isCreator;
        if (groupFilter === 'member') return !isCreator && !isAdmin;

        return true;
    });

    const createdGroupCount = groups.filter(g => (g.groupCreater?._id || g.groupCreater) === user?._id).length;
    const adminGroupCount = groups.filter(g => (g.groupCreater?._id || g.groupCreater) !== user?._id && Array.isArray(g.groupAdmin) && g.groupAdmin.some(a => (a?._id || a) === user?._id)).length;
    const memberGroupCount = groups.length - createdGroupCount - adminGroupCount;

    // Counts for pills
    const hostedCount = events.filter(e => (e.userId?._id || e.userId) === user?._id).length;
    const joinedCount = events.length - hostedCount;
    const startedCount = events.filter(e => e.event_status === 'started').length;
    const finishedCount = events.filter(e => e.event_status === 'finished').length;

    // Handle Edit Event Date Success
    const handleEventDateUpdated = (updatedEvent) => {
        setEvents((prev) =>
            prev.map((ev) => (ev._id === updatedEvent._id ? { ...ev, ...updatedEvent } : ev))
        );
    };

    // Handle Assign / Extend Plan Submission
    const handlePlanSubmit = async (actionType) => {
        if (!user?._id) {
            toast.error('User not identified');
            return;
        }

        const days = actionType === 'extend' ? Number(extendDays) : Number(customPlanDays);
        if (!days || days <= 0) {
            toast.error('Please enter valid plan days');
            return;
        }

        try {
            setPlanLoading(true);
            const payload = {
                userId: user._id,
                actionType: actionType,
                planDays: days,
                planType: actionType === 'extend' ? (currentPlan?.planType || 'Extended Premium') : customPlanType,
                planPrice: actionType === 'extend' ? 0 : Number(customPlanPrice),
                planId: selectedPlanId || undefined
            };

            const resp = await AssignUserPlan(payload);
            const data = (resp && typeof resp === 'string') ? DecryptData(resp) : resp;

            if (data?.status) {
                toast.success(data?.message || 'Plan updated successfully!');
                if (data?.data?.user) {
                    setUser(data.data.user);
                } else if (data?.data?.plan) {
                    setUser((prev) => ({
                        ...prev,
                        plan: [data.data.plan]
                    }));
                }
            } else {
                toast.error(data?.message || 'Failed to update plan');
            }
        } catch (err) {
            console.error('Plan submit error:', err);
            toast.error('An error occurred while updating user plan');
        } finally {
            setPlanLoading(false);
        }
    };

    // Calculate extended date preview
    const calculateNewEndDate = (daysToAdd) => {
        const now = new Date();
        const base = (currentPlan?.EndDate && new Date(currentPlan.EndDate) > now)
            ? new Date(currentPlan.EndDate)
            : now;
        const newEnd = new Date(base.getTime() + Number(daysToAdd) * 24 * 60 * 60 * 1000);
        return newEnd.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
        <div className='dashbaord_wrapper'>
            <Sidebar />
            <div className='dashboard_content'>
                <Header />
                <Container fluid className='px-4 py-3'>
                    {/* Top Navigation */}
                    <div className='d-flex align-items-center justify-content-between mb-3'>
                        <button
                            className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-2'
                            onClick={() => navigate('/users')}
                            style={{ color: '#fff', borderColor: '#444' }}
                        >
                            <FaArrowLeft /> Back to Users
                        </button>
                        <span className='text-muted small'>User ID: <span className='text-warning'>{user?._id || 'N/A'}</span></span>
                    </div>

                    {/* Profile Summary Card */}
                    <div
                        className='p-4 mb-4'
                        style={{
                            background: '#181818',
                            borderRadius: '14px',
                            border: '1px solid #2d2d2d',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
                        }}
                    >
                        <Row className='align-items-center'>
                            <Col xs={12} md={2} className='text-center mb-3 mb-md-0'>
                                <div
                                    style={{
                                        width: '90px',
                                        height: '90px',
                                        margin: '0 auto',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        border: isPremiumActive ? '3px solid #f8be2d' : '3px solid #444',
                                        background: '#222'
                                    }}
                                >
                                    <img
                                        src={user?.profile ? `${Config.AWS_CDN_URL}${user.profile}` : DefultImage}
                                        alt='Profile'
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                            </Col>

                            <Col xs={12} md={7}>
                                <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
                                    <h4 className='text-white mb-0 fw-bold'>{user?.name || user?.username || 'Unnamed User'}</h4>
                                    {isPremiumActive && (
                                        <span
                                            className='badge d-flex align-items-center gap-1'
                                            style={{ background: '#f8be2d', color: '#000', fontWeight: 700 }}
                                        >
                                            <FaCrown size={12} /> PREMIUM
                                        </span>
                                    )}
                                    <span
                                        className={`badge ${user?.blockStatus === 'blocked' ? 'bg-danger' : 'bg-success'}`}
                                    >
                                        {user?.blockStatus === 'blocked' ? 'Blocked' : 'Active'}
                                    </span>
                                </div>
                                <p className='text-muted mb-2' style={{ fontSize: '13px' }}>
                                    @{user?.username || 'no-username'} &bull; {user?.email || 'no-email'}
                                    {user?.mobileNumber ? ` &bull; ${user.mobileNumber}` : ''}
                                </p>

                                <div className='d-flex flex-wrap gap-3 text-secondary' style={{ fontSize: '12px' }}>
                                    <div><strong className='text-white'>Age:</strong> {user?.age || 'N/A'}</div>
                                    <div><strong className='text-white'>Gender:</strong> {user?.gender || 'N/A'}</div>
                                    <div><strong className='text-white'>Followers:</strong> {user?.followersCount || 0}</div>
                                    <div><strong className='text-white'>Following:</strong> {user?.followingCount || 0}</div>
                                    <div><strong className='text-white'>Account:</strong> {user?.delete ? 'Deleted' : 'Active'}</div>
                                </div>
                            </Col>

                            <Col xs={12} md={3} className='text-md-end mt-3 mt-md-0'>
                                <div
                                    className='p-3'
                                    style={{
                                        background: '#202020',
                                        borderRadius: '10px',
                                        border: isPremiumActive ? '1px solid #f8be2d44' : '1px solid #333'
                                    }}
                                >
                                    <div className='small text-muted'>Subscription Plan</div>
                                    <div className='fw-bold' style={{ color: isPremiumActive ? '#f8be2d' : '#999' }}>
                                        {isPremiumActive ? (currentPlan?.planType || 'Premium Active') : 'Free Tier'}
                                    </div>
                                    {isPremiumActive && currentPlan?.EndDate && (
                                        <div className='text-muted mt-1' style={{ fontSize: '11px' }}>
                                            Expires: {new Date(currentPlan.EndDate).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* 4 Tabs Bar */}
                    <div
                        className='d-flex align-items-center gap-2 mb-4 p-1'
                        style={{
                            background: '#121212',
                            borderRadius: '10px',
                            border: '1px solid #282828',
                            overflowX: 'auto'
                        }}
                    >
                        {[
                            { id: 'events', label: 'Events', icon: <FaCalendarAlt />, count: events.length },
                            { id: 'groups', label: 'Groups', icon: <FaUsers />, count: groups.length },
                            { id: 'premium', label: 'Make Premium', icon: <FaCrown />, badge: isPremiumActive ? 'Active' : null },
                            { id: 'following', label: 'Follow & Requests', icon: <FaUserFriends />, count: (user?.followersCount || 0) + (user?.followingCount || 0), badge: followDetails?.counts?.pendingReceived > 0 ? `${followDetails.counts.pendingReceived} Pending` : null }
                        ].map((tab) => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type='button'
                                    onClick={() => setActiveTab(tab.id)}
                                    className='btn d-flex align-items-center gap-2 px-4 py-2'
                                    style={{
                                        background: isActive ? '#f8be2d' : 'transparent',
                                        color: isActive ? '#000' : '#aaa',
                                        fontWeight: isActive ? 700 : 500,
                                        borderRadius: '8px',
                                        border: 'none',
                                        transition: 'all 0.2s ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {tab.icon}
                                    <span>{tab.label}</span>
                                    {tab.count !== undefined && (
                                        <span
                                            className='badge'
                                            style={{
                                                background: isActive ? '#000' : '#282828',
                                                color: isActive ? '#f8be2d' : '#888',
                                                fontSize: '11px'
                                            }}
                                        >
                                            {tab.count}
                                        </span>
                                    )}
                                    {tab.badge && (
                                        <span
                                            className='badge'
                                            style={{
                                                background: isActive ? '#000' : '#22c55e22',
                                                color: isActive ? '#fff' : '#22c55e',
                                                fontSize: '10px'
                                            }}
                                        >
                                            {tab.badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* TAB CONTENT */}

                    {/* TAB 1: EVENTS */}
                    {activeTab === 'events' && (
                        <div>
                            {/* Controls: Search, Filters, View toggle */}
                            <div
                                className='p-3 mb-3 d-flex flex-wrap align-items-center justify-content-between gap-3'
                                style={{ background: '#181818', borderRadius: '10px', border: '1px solid #2d2d2d' }}
                            >
                                {/* Filter Pills */}
                                <div className='d-flex flex-wrap gap-2 align-items-center'>
                                    {[
                                        { id: 'all', label: `All (${events.length})` },
                                        { id: 'hosted', label: `Hosted / Creator (${hostedCount})` },
                                        { id: 'joined', label: `Joined / Participant (${joinedCount})` },
                                        { id: 'started', label: `Started / Active (${startedCount})` },
                                        { id: 'finished', label: `Finished (${finishedCount})` }
                                    ].map((f) => (
                                        <button
                                            key={f.id}
                                            type='button'
                                            onClick={() => setEventFilter(f.id)}
                                            className='btn btn-sm'
                                            style={{
                                                background: eventFilter === f.id ? '#2a2a2a' : '#141414',
                                                color: eventFilter === f.id ? '#f8be2d' : '#888',
                                                border: eventFilter === f.id ? '1px solid #f8be2d' : '1px solid #333',
                                                borderRadius: '20px',
                                                padding: '4px 12px',
                                                fontSize: '12px',
                                                fontWeight: eventFilter === f.id ? 600 : 400
                                            }}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Right Side: Search and View Mode */}
                                <div className='d-flex align-items-center gap-2'>
                                    <input
                                        type='text'
                                        className='form-control form-control-sm'
                                        placeholder='Search events...'
                                        value={eventSearch}
                                        onChange={(e) => setEventSearch(e.target.value)}
                                        style={{
                                            background: '#121212',
                                            color: '#fff',
                                            border: '1px solid #444',
                                            borderRadius: '6px',
                                            width: '200px'
                                        }}
                                    />
                                    <div className='btn-group btn-group-sm'>
                                        <button
                                            className={`btn ${viewMode === 'cards' ? 'btn-warning' : 'btn-dark'}`}
                                            style={{ color: viewMode === 'cards' ? '#000' : '#fff' }}
                                            onClick={() => setViewMode('cards')}
                                            title='Card View'
                                        >
                                            <FaThLarge />
                                        </button>
                                        <button
                                            className={`btn ${viewMode === 'table' ? 'btn-warning' : 'btn-dark'}`}
                                            style={{ color: viewMode === 'table' ? '#000' : '#fff' }}
                                            onClick={() => setViewMode('table')}
                                            title='Table View'
                                        >
                                            <FaList />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Loading State */}
                            {eventsLoading && (
                                <div className='text-center py-5'>
                                    <div className='spinner-border text-warning' role='status' />
                                    <p className='text-muted mt-2'>Loading user events...</p>
                                </div>
                            )}

                            {/* Empty State */}
                            {!eventsLoading && filteredEvents.length === 0 && (
                                <div
                                    className='text-center py-5'
                                    style={{ background: '#181818', borderRadius: '12px', border: '1px dashed #333' }}
                                >
                                    <FaCalendarAlt size={36} className='text-muted mb-2' />
                                    <h6 className='text-white'>No Events Found</h6>
                                    <p className='text-muted small'>This user has not created or joined any events matching the criteria.</p>
                                </div>
                            )}

                            {/* Cards View */}
                            {!eventsLoading && filteredEvents.length > 0 && viewMode === 'cards' && (
                                <Row>
                                    {filteredEvents.map((ev) => {
                                        const isHost = (ev.userId?._id || ev.userId) === user?._id;
                                        const statusColor =
                                            ev.event_status === 'started'
                                                ? '#22c55e'
                                                : ev.event_status === 'finished'
                                                ? '#ef4444'
                                                : '#f59e0b';

                                        const firstImage = Array.isArray(ev.image) && ev.image.length > 0 ? ev.image[0] : null;

                                        return (
                                            <Col key={ev._id} xs={12} md={6} lg={4} className='mb-4'>
                                                <div
                                                    className='h-100 d-flex flex-column'
                                                    style={{
                                                        background: '#181818',
                                                        borderRadius: '12px',
                                                        border: '1px solid #2d2d2d',
                                                        overflow: 'hidden',
                                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                                    }}
                                                >
                                                    {/* Card Image Banner */}
                                                    <div
                                                        style={{
                                                            height: '140px',
                                                            background: '#222',
                                                            position: 'relative',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        {firstImage ? (
                                                            <img
                                                                src={`${Config.AWS_CDN_URL}${firstImage}`}
                                                                alt='Event'
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className='d-flex align-items-center justify-content-center h-100 text-muted'>
                                                                <FaCalendarAlt size={40} />
                                                            </div>
                                                        )}

                                                        {/* Role Badge */}
                                                        <span
                                                            className='badge position-absolute top-0 start-0 m-2'
                                                            style={{
                                                                background: isHost ? '#f8be2d' : '#3b82f6',
                                                                color: isHost ? '#000' : '#fff',
                                                                fontWeight: 700
                                                            }}
                                                        >
                                                            {isHost ? 'Host / Creator' : 'Joined Participant'}
                                                        </span>

                                                        {/* Status Badge */}
                                                        <span
                                                            className='badge position-absolute top-0 end-0 m-2'
                                                            style={{ background: statusColor, color: '#fff' }}
                                                        >
                                                            {ev.event_status || 'upcoming'}
                                                        </span>
                                                    </div>

                                                    {/* Card Body */}
                                                    <div className='p-3 d-flex flex-column flex-grow-1'>
                                                        <h6 className='text-white fw-bold mb-1 text-truncate' title={ev.eventTitle}>
                                                            {ev.eventTitle || 'Untitled Event'}
                                                        </h6>

                                                        {/* Dates */}
                                                        <div className='text-muted small mb-2' style={{ fontSize: '11px' }}>
                                                            <div>
                                                                <span className='text-secondary'>Start: </span>
                                                                <span className='text-white'>
                                                                    {ev.startDate ? new Date(ev.startDate).toLocaleDateString() : 'N/A'}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <span className='text-secondary'>End: </span>
                                                                <span className='text-warning fw-bold'>
                                                                    {ev.endDate ? new Date(ev.endDate).toLocaleString() : 'N/A'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Description snippet */}
                                                        <p
                                                            className='text-secondary small mb-3 flex-grow-1'
                                                            style={{
                                                                fontSize: '12px',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            {ev.description || 'No description provided.'}
                                                        </p>

                                                        {/* Quick Modals info buttons */}
                                                        <div className='d-flex flex-wrap gap-2 mb-3'>
                                                            <button
                                                                type='button'
                                                                className='btn btn-dark btn-sm py-1 px-2'
                                                                style={{ fontSize: '11px', border: '1px solid #333' }}
                                                                onClick={() => {
                                                                    setSelectedEventDesc(ev);
                                                                    setShowDescModal(true);
                                                                }}
                                                            >
                                                                <FaInfoCircle className='me-1' /> Description
                                                            </button>
                                                            <button
                                                                type='button'
                                                                className='btn btn-dark btn-sm py-1 px-2'
                                                                style={{ fontSize: '11px', border: '1px solid #333' }}
                                                                onClick={() => {
                                                                    setSelectedEventImages(ev);
                                                                    setShowImagesModal(true);
                                                                }}
                                                            >
                                                                <FaImage className='me-1' /> Images ({ev.image?.length || 0})
                                                            </button>
                                                            <button
                                                                type='button'
                                                                className='btn btn-dark btn-sm py-1 px-2'
                                                                style={{ fontSize: '11px', border: '1px solid #333' }}
                                                                onClick={() => {
                                                                    setSelectedAddress(ev.eventAddress);
                                                                    setShowAddressModal(true);
                                                                }}
                                                            >
                                                                <FaMapMarkerAlt className='me-1' /> Address
                                                            </button>
                                                        </div>

                                                        {/* Action: Edit Event End Date */}
                                                        <div className='pt-2 border-top border-secondary'>
                                                            <button
                                                                type='button'
                                                                className='btn btn-warning btn-sm w-100 fw-bold d-flex align-items-center justify-content-center gap-2'
                                                                style={{ background: '#f8be2d', color: '#000', border: 'none' }}
                                                                onClick={() => {
                                                                    setSelectedEventForEditDate(ev);
                                                                    setShowEditDateModal(true);
                                                                }}
                                                            >
                                                                <FaCalendarAlt /> Edit End Date
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            )}

                            {/* Table View */}
                            {!eventsLoading && filteredEvents.length > 0 && viewMode === 'table' && (
                                <div
                                    className='table-responsive'
                                    style={{ background: '#181818', borderRadius: '12px', border: '1px solid #2d2d2d' }}
                                >
                                    <table className='table table-dark table-hover mb-0' style={{ background: '#181818' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #333', color: '#aaa', fontSize: '13px' }}>
                                                <th>#</th>
                                                <th>Event Title</th>
                                                <th>Role</th>
                                                <th>Start Date</th>
                                                <th>End Date</th>
                                                <th>Status</th>
                                                <th>Modals</th>
                                                <th className='text-center'>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody style={{ fontSize: '13px' }}>
                                            {filteredEvents.map((ev, idx) => {
                                                const isHost = (ev.userId?._id || ev.userId) === user?._id;
                                                const statusColor =
                                                    ev.event_status === 'started'
                                                        ? '#22c55e'
                                                        : ev.event_status === 'finished'
                                                        ? '#ef4444'
                                                        : '#f59e0b';

                                                return (
                                                    <tr key={ev._id} style={{ verticalAlign: 'middle', borderBottom: '1px solid #262626' }}>
                                                        <td className='text-muted'>{idx + 1}</td>
                                                        <td>
                                                            <div className='fw-bold text-white'>{ev.eventTitle || 'Untitled'}</div>
                                                            <small className='text-muted'>{ev.eventAddress ? ev.eventAddress.substring(0, 30) + '...' : ''}</small>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className='badge'
                                                                style={{
                                                                    background: isHost ? '#f8be2d' : '#3b82f6',
                                                                    color: isHost ? '#000' : '#fff'
                                                                }}
                                                            >
                                                                {isHost ? 'Host' : 'Participant'}
                                                            </span>
                                                        </td>
                                                        <td className='text-muted'>
                                                            {ev.startDate ? new Date(ev.startDate).toLocaleDateString() : 'N/A'}
                                                        </td>
                                                        <td className='text-warning fw-bold'>
                                                            {ev.endDate ? new Date(ev.endDate).toLocaleString() : 'N/A'}
                                                        </td>
                                                        <td>
                                                            <span className='badge' style={{ background: statusColor }}>
                                                                {ev.event_status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className='d-flex gap-1'>
                                                                <button
                                                                    type='button'
                                                                    className='btn btn-dark btn-sm py-0 px-2'
                                                                    onClick={() => {
                                                                        setSelectedEventDesc(ev);
                                                                        setShowDescModal(true);
                                                                    }}
                                                                    title='View Description'
                                                                >
                                                                    Desc
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className='btn btn-dark btn-sm py-0 px-2'
                                                                    onClick={() => {
                                                                        setSelectedEventImages(ev);
                                                                        setShowImagesModal(true);
                                                                    }}
                                                                    title='View Images'
                                                                >
                                                                    Images
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className='btn btn-dark btn-sm py-0 px-2'
                                                                    onClick={() => {
                                                                        setSelectedAddress(ev.eventAddress);
                                                                        setShowAddressModal(true);
                                                                    }}
                                                                    title='View Address'
                                                                >
                                                                    Loc
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className='text-center'>
                                                            <button
                                                                type='button'
                                                                className='btn btn-warning btn-sm fw-bold d-inline-flex align-items-center gap-1'
                                                                style={{ background: '#f8be2d', color: '#000', border: 'none' }}
                                                                onClick={() => {
                                                                    setSelectedEventForEditDate(ev);
                                                                    setShowEditDateModal(true);
                                                                }}
                                                            >
                                                                <FaCalendarAlt size={12} /> Edit Date
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 2: GROUPS (List of user's groups) */}
                    {activeTab === 'groups' && (
                        <div>
                            {/* Controls: Search, Filters, View toggle */}
                            <div
                                className='p-3 mb-3 d-flex flex-wrap align-items-center justify-content-between gap-3'
                                style={{ background: '#181818', borderRadius: '10px', border: '1px solid #2d2d2d' }}
                            >
                                {/* Filter Pills */}
                                <div className='d-flex flex-wrap gap-2 align-items-center'>
                                    {[
                                        { id: 'all', label: `All Groups (${groups.length})` },
                                        { id: 'created', label: `Created by User (${createdGroupCount})` },
                                        { id: 'admin', label: `Admin (${adminGroupCount})` },
                                        { id: 'member', label: `Member / Joined (${memberGroupCount})` }
                                    ].map((f) => (
                                        <button
                                            key={f.id}
                                            type='button'
                                            onClick={() => setGroupFilter(f.id)}
                                            className='btn btn-sm'
                                            style={{
                                                background: groupFilter === f.id ? '#2a2a2a' : '#141414',
                                                color: groupFilter === f.id ? '#f8be2d' : '#888',
                                                border: groupFilter === f.id ? '1px solid #f8be2d' : '1px solid #333',
                                                borderRadius: '20px',
                                                padding: '4px 12px',
                                                fontSize: '12px',
                                                fontWeight: groupFilter === f.id ? 600 : 400
                                            }}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Right Side: Search and View Mode */}
                                <div className='d-flex align-items-center gap-2'>
                                    <input
                                        type='text'
                                        className='form-control form-control-sm'
                                        placeholder='Search groups by name, location...'
                                        value={groupSearch}
                                        onChange={(e) => setGroupSearch(e.target.value)}
                                        style={{
                                            background: '#121212',
                                            color: '#fff',
                                            border: '1px solid #444',
                                            borderRadius: '6px',
                                            width: '240px'
                                        }}
                                    />
                                    <div className='btn-group btn-group-sm'>
                                        <button
                                            className={`btn ${groupViewMode === 'cards' ? 'btn-warning' : 'btn-dark'}`}
                                            style={{ color: groupViewMode === 'cards' ? '#000' : '#fff' }}
                                            onClick={() => setGroupViewMode('cards')}
                                            title='Card View'
                                        >
                                            <FaThLarge />
                                        </button>
                                        <button
                                            className={`btn ${groupViewMode === 'table' ? 'btn-warning' : 'btn-dark'}`}
                                            style={{ color: groupViewMode === 'table' ? '#000' : '#fff' }}
                                            onClick={() => setGroupViewMode('table')}
                                            title='Table View'
                                        >
                                            <FaList />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Loading State */}
                            {groupsLoading && (
                                <div className='text-center py-5'>
                                    <div className='spinner-border text-warning' role='status' />
                                    <p className='text-muted mt-2'>Loading user groups...</p>
                                </div>
                            )}

                            {/* Empty State */}
                            {!groupsLoading && filteredGroups.length === 0 && (
                                <div
                                    className='text-center py-5'
                                    style={{ background: '#181818', borderRadius: '12px', border: '1px dashed #333' }}
                                >
                                    <FaUsers size={36} className='text-muted mb-2' />
                                    <h6 className='text-white'>No Groups Found</h6>
                                    <p className='text-muted small'>This user is not a creator, admin, or member of any groups matching the criteria.</p>
                                </div>
                            )}

                            {/* Groups Cards View */}
                            {!groupsLoading && filteredGroups.length > 0 && groupViewMode === 'cards' && (
                                <Row>
                                    {filteredGroups.map((grp) => {
                                        const isCreator = (grp.groupCreater?._id || grp.groupCreater) === user?._id;
                                        const isAdmin = Array.isArray(grp.groupAdmin) && grp.groupAdmin.some(a => (a?._id || a) === user?._id);
                                        const memberTotal = (grp.members?.length || 0) + (grp.joinMembers?.length || 0);
                                        const groupImg = grp.Image ? `${Config.AWS_CDN_URL}${grp.Image}` : DefultImage;

                                        return (
                                            <Col key={grp._id} xs={12} md={6} lg={4} className='mb-4'>
                                                <div
                                                    className='h-100 d-flex flex-column'
                                                    style={{
                                                        background: '#181818',
                                                        borderRadius: '12px',
                                                        border: '1px solid #2d2d2d',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    {/* Card Header Banner */}
                                                    <div
                                                        style={{
                                                            height: '130px',
                                                            background: '#202020',
                                                            position: 'relative',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        <img
                                                            src={groupImg}
                                                            alt={grp.chatName}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />

                                                        {/* Role Badge */}
                                                        <span
                                                            className='badge position-absolute top-0 start-0 m-2'
                                                            style={{
                                                                background: isCreator ? '#f8be2d' : (isAdmin ? '#f97316' : '#3b82f6'),
                                                                color: isCreator ? '#000' : '#fff',
                                                                fontWeight: 700
                                                            }}
                                                        >
                                                            {isCreator ? 'Creator / Owner' : (isAdmin ? 'Group Admin' : 'Member')}
                                                        </span>

                                                        {/* Privacy Badge */}
                                                        <span
                                                            className='badge position-absolute top-0 end-0 m-2 d-flex align-items-center gap-1'
                                                            style={{
                                                                background: grp.isPrivate ? '#ef4444' : '#22c55e',
                                                                color: '#fff'
                                                            }}
                                                        >
                                                            {grp.isPrivate ? <><MdLock size={12} /> Private</> : <><MdPublic size={12} /> Public</>}
                                                        </span>
                                                    </div>

                                                    {/* Card Body */}
                                                    <div className='p-3 d-flex flex-column flex-grow-1'>
                                                        <h6 className='text-white fw-bold mb-1 text-truncate' title={grp.chatName}>
                                                            {grp.chatName || 'Untitled Group'}
                                                        </h6>

                                                        <div className='d-flex flex-wrap gap-3 text-muted small mb-2' style={{ fontSize: '11px' }}>
                                                            <div>
                                                                <span className='text-secondary'>Members: </span>
                                                                <span className='text-success fw-bold'>{memberTotal}</span>
                                                            </div>
                                                            <div>
                                                                <span className='text-secondary'>Created: </span>
                                                                <span className='text-white'>{grp.createdAt ? new Date(grp.createdAt).toLocaleDateString() : 'N/A'}</span>
                                                            </div>
                                                        </div>

                                                        {grp.groupAddress && (
                                                            <div className='text-muted small mb-2 text-truncate' style={{ fontSize: '11px' }}>
                                                                <FaMapMarkerAlt className='text-warning me-1' /> {grp.groupAddress}
                                                            </div>
                                                        )}

                                                        <p
                                                            className='text-secondary small mb-3 flex-grow-1'
                                                            style={{
                                                                fontSize: '12px',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            {grp.groupDiscription || 'No description available.'}
                                                        </p>

                                                        {/* Group Admin preview */}
                                                        {Array.isArray(grp.groupAdmin) && grp.groupAdmin.length > 0 && (
                                                            <div className='pt-2 border-top border-secondary'>
                                                                <div className='text-muted small' style={{ fontSize: '11px' }}>
                                                                    <strong className='text-secondary'>Admins: </strong>
                                                                    <span className='text-white'>
                                                                        {grp.groupAdmin.map(a => a?.username || a?.name || 'Admin').join(', ')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            )}

                            {/* Groups Table View */}
                            {!groupsLoading && filteredGroups.length > 0 && groupViewMode === 'table' && (
                                <div
                                    className='table-responsive'
                                    style={{ background: '#181818', borderRadius: '12px', border: '1px solid #2d2d2d' }}
                                >
                                    <table className='table table-dark table-hover mb-0' style={{ background: '#181818' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #333', color: '#aaa', fontSize: '13px' }}>
                                                <th>#</th>
                                                <th>Profile</th>
                                                <th>Group Name</th>
                                                <th>User Role</th>
                                                <th>Privacy</th>
                                                <th>Members</th>
                                                <th>Created Date</th>
                                                <th>Address</th>
                                            </tr>
                                        </thead>
                                        <tbody style={{ fontSize: '13px' }}>
                                            {filteredGroups.map((grp, idx) => {
                                                const isCreator = (grp.groupCreater?._id || grp.groupCreater) === user?._id;
                                                const isAdmin = Array.isArray(grp.groupAdmin) && grp.groupAdmin.some(a => (a?._id || a) === user?._id);
                                                const memberTotal = (grp.members?.length || 0) + (grp.joinMembers?.length || 0);
                                                const groupImg = grp.Image ? `${Config.AWS_CDN_URL}${grp.Image}` : DefultImage;

                                                return (
                                                    <tr key={grp._id} style={{ verticalAlign: 'middle', borderBottom: '1px solid #262626' }}>
                                                        <td className='text-muted'>{idx + 1}</td>
                                                        <td>
                                                            <div
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '50%',
                                                                    overflow: 'hidden',
                                                                    background: '#222'
                                                                }}
                                                            >
                                                                <img
                                                                    src={groupImg}
                                                                    alt='Group'
                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className='fw-bold text-white'>{grp.chatName || 'Untitled Group'}</div>
                                                            <small className='text-muted'>{grp.groupDiscription ? grp.groupDiscription.substring(0, 30) + '...' : ''}</small>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className='badge'
                                                                style={{
                                                                    background: isCreator ? '#f8be2d' : (isAdmin ? '#f97316' : '#3b82f6'),
                                                                    color: isCreator ? '#000' : '#fff'
                                                                }}
                                                            >
                                                                {isCreator ? 'Owner' : (isAdmin ? 'Admin' : 'Member')}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${grp.isPrivate ? 'bg-danger' : 'bg-success'}`}>
                                                                {grp.isPrivate ? 'Private' : 'Public'}
                                                            </span>
                                                        </td>
                                                        <td className='text-success fw-bold'>{memberTotal}</td>
                                                        <td className='text-muted'>{grp.createdAt ? new Date(grp.createdAt).toLocaleDateString() : 'N/A'}</td>
                                                        <td className='text-muted small'>{grp.groupAddress || 'N/A'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 3: MAKE PREMIUM (Assign new plan or Extend active plan) */}
                    {activeTab === 'premium' && (
                        <div>
                            {/* Current Plan Status Card */}
                            <div
                                className='p-4 mb-4'
                                style={{
                                    background: '#181818',
                                    borderRadius: '12px',
                                    border: isPremiumActive ? '1px solid #f8be2d55' : '1px solid #333'
                                }}
                            >
                                <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3'>
                                    <div>
                                        <h5 className='text-white mb-1 fw-bold d-flex align-items-center gap-2'>
                                            <FaCrown className='text-warning' /> Current Subscription Overview
                                        </h5>
                                        <p className='text-muted mb-0 small'>
                                            Manage premium status, assign new plans, or extend active duration.
                                        </p>
                                    </div>
                                    <span
                                        className='badge px-3 py-2'
                                        style={{
                                            background: isPremiumActive ? '#22c55e22' : '#333',
                                            color: isPremiumActive ? '#22c55e' : '#aaa',
                                            fontSize: '12px',
                                            border: isPremiumActive ? '1px solid #22c55e55' : '1px solid #444'
                                        }}
                                    >
                                        {isPremiumActive ? 'ACTIVE PREMIUM USER' : 'FREE USER (NO ACTIVE PLAN)'}
                                    </span>
                                </div>

                                <Row className='g-3'>
                                    <Col xs={12} sm={6} md={3}>
                                        <div className='p-3' style={{ background: '#202020', borderRadius: '8px' }}>
                                            <div className='text-muted small'>Plan Type</div>
                                            <div className='text-white fw-bold'>{currentPlan?.planType || 'None'}</div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={3}>
                                        <div className='p-3' style={{ background: '#202020', borderRadius: '8px' }}>
                                            <div className='text-muted small'>Plan Days</div>
                                            <div className='text-white fw-bold'>{currentPlan?.planDays || 0} Days</div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={3}>
                                        <div className='p-3' style={{ background: '#202020', borderRadius: '8px' }}>
                                            <div className='text-muted small'>Start Date</div>
                                            <div className='text-white fw-bold'>
                                                {currentPlan?.startDate ? new Date(currentPlan.startDate).toLocaleDateString() : 'N/A'}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={3}>
                                        <div className='p-3' style={{ background: '#202020', borderRadius: '8px' }}>
                                            <div className='text-muted small'>Expiration Date</div>
                                            <div className='text-warning fw-bold'>
                                                {currentPlan?.EndDate ? new Date(currentPlan.EndDate).toLocaleDateString() : 'N/A'}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Dual Mode Switch: Extend Plan vs Assign New Plan */}
                            <div
                                className='p-4'
                                style={{ background: '#181818', borderRadius: '12px', border: '1px solid #2d2d2d' }}
                            >
                                <div className='d-flex gap-2 mb-4 border-bottom border-secondary pb-3'>
                                    <button
                                        type='button'
                                        className='btn'
                                        onClick={() => setPremiumSubTab('extend')}
                                        style={{
                                            background: premiumSubTab === 'extend' ? '#f8be2d' : '#222',
                                            color: premiumSubTab === 'extend' ? '#000' : '#fff',
                                            fontWeight: 600,
                                            borderRadius: '8px'
                                        }}
                                    >
                                        Extend Existing Plan (+Days)
                                    </button>
                                    <button
                                        type='button'
                                        className='btn'
                                        onClick={() => setPremiumSubTab('assign')}
                                        style={{
                                            background: premiumSubTab === 'assign' ? '#f8be2d' : '#222',
                                            color: premiumSubTab === 'assign' ? '#000' : '#fff',
                                            fontWeight: 600,
                                            borderRadius: '8px'
                                        }}
                                    >
                                        Assign New Plan
                                    </button>
                                </div>

                                {/* Option A: Extend Existing Plan */}
                                {premiumSubTab === 'extend' && (
                                    <div>
                                        <h6 className='text-white fw-bold mb-3'>
                                            Extend Subscription Duration
                                        </h6>
                                        <p className='text-muted small mb-3'>
                                            Add additional days to the user's current subscription. If expired or free, days will count from today.
                                        </p>

                                        {/* Quick Days Pills */}
                                        <div className='mb-3'>
                                            <label className='form-label text-white small fw-bold mb-1'>Quick Add Presets:</label>
                                            <div className='d-flex flex-wrap gap-2'>
                                                {[7, 15, 30, 60, 90, 180, 365].map((d) => (
                                                    <button
                                                        key={d}
                                                        type='button'
                                                        onClick={() => setExtendDays(d)}
                                                        className='btn btn-sm'
                                                        style={{
                                                            background: extendDays === d ? '#f8be2d' : '#222',
                                                            color: extendDays === d ? '#000' : '#fff',
                                                            border: extendDays === d ? '1px solid #f8be2d' : '1px solid #444',
                                                            borderRadius: '20px',
                                                            padding: '4px 14px',
                                                            fontWeight: extendDays === d ? 700 : 400
                                                        }}
                                                    >
                                                        +{d} Days
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Custom Days Input */}
                                        <Row className='align-items-end g-3 mb-4'>
                                            <Col xs={12} sm={6} md={4}>
                                                <label className='form-label text-white small fw-bold mb-1'>Number of Days to Add:</label>
                                                <input
                                                    type='number'
                                                    min='1'
                                                    className='form-control'
                                                    style={{ background: '#121212', color: '#fff', border: '1px solid #444' }}
                                                    value={extendDays}
                                                    onChange={(e) => setExtendDays(Number(e.target.value))}
                                                />
                                            </Col>

                                            <Col xs={12} sm={6} md={8}>
                                                <div
                                                    className='p-3'
                                                    style={{ background: '#202020', borderRadius: '8px', borderLeft: '4px solid #f8be2d' }}
                                                >
                                                    <span className='text-muted small'>Predicted Expiration Date after extension: </span>
                                                    <span className='text-warning fw-bold ms-2'>{calculateNewEndDate(extendDays)}</span>
                                                </div>
                                            </Col>
                                        </Row>

                                        <button
                                            type='button'
                                            className='btn btn-warning px-4 py-2 fw-bold d-inline-flex align-items-center gap-2'
                                            style={{ background: '#f8be2d', color: '#000', border: 'none' }}
                                            onClick={() => handlePlanSubmit('extend')}
                                            disabled={planLoading}
                                        >
                                            <FaCrown /> {planLoading ? 'Processing...' : `Extend Subscription (+${extendDays} Days)`}
                                        </button>
                                    </div>
                                )}

                                {/* Option B: Assign New Plan */}
                                {premiumSubTab === 'assign' && (
                                    <div>
                                        <h6 className='text-white fw-bold mb-3'>
                                            Assign Fresh Premium Plan
                                        </h6>
                                        <p className='text-muted small mb-3'>
                                            Select from predefined admin plans or customize a new plan for this user.
                                        </p>

                                        <Row className='g-3 mb-4'>
                                            {/* Predefined Plan Dropdown */}
                                            <Col xs={12} md={6}>
                                                <label className='form-label text-white small fw-bold mb-1'>Select Predefined Plan (Optional):</label>
                                                <select
                                                    className='form-select'
                                                    style={{ background: '#121212', color: '#fff', border: '1px solid #444' }}
                                                    value={selectedPlanId}
                                                    onChange={(e) => {
                                                        const pId = e.target.value;
                                                        setSelectedPlanId(pId);
                                                        const found = availablePlans.find(p => p._id === pId);
                                                        if (found) {
                                                            setCustomPlanType(found.planType || found.planName || 'Premium');
                                                            setCustomPlanDays(found.planDays || found.duration || 30);
                                                            setCustomPlanPrice(found.planPrice || found.price || 0);
                                                        }
                                                    }}
                                                >
                                                    <option value=''>-- Custom Plan --</option>
                                                    {availablePlans.map((pln) => (
                                                        <option key={pln._id} value={pln._id}>
                                                            {pln.planType || pln.planName || 'Plan'} - {pln.planDays || 30} Days (${pln.planPrice || 0})
                                                        </option>
                                                    ))}
                                                </select>
                                            </Col>

                                            {/* Plan Name / Type */}
                                            <Col xs={12} md={6}>
                                                <label className='form-label text-white small fw-bold mb-1'>Plan Type / Title:</label>
                                                <input
                                                    type='text'
                                                    className='form-control'
                                                    style={{ background: '#121212', color: '#fff', border: '1px solid #444' }}
                                                    value={customPlanType}
                                                    onChange={(e) => setCustomPlanType(e.target.value)}
                                                />
                                            </Col>

                                            {/* Plan Duration in Days */}
                                            <Col xs={12} sm={6} md={6}>
                                                <label className='form-label text-white small fw-bold mb-1'>Duration (Days):</label>
                                                <input
                                                    type='number'
                                                    min='1'
                                                    className='form-control'
                                                    style={{ background: '#121212', color: '#fff', border: '1px solid #444' }}
                                                    value={customPlanDays}
                                                    onChange={(e) => setCustomPlanDays(Number(e.target.value))}
                                                />
                                            </Col>

                                            {/* Plan Price */}
                                            <Col xs={12} sm={6} md={6}>
                                                <label className='form-label text-white small fw-bold mb-1'>Plan Price ($):</label>
                                                <input
                                                    type='number'
                                                    min='0'
                                                    className='form-control'
                                                    style={{ background: '#121212', color: '#fff', border: '1px solid #444' }}
                                                    value={customPlanPrice}
                                                    onChange={(e) => setCustomPlanPrice(Number(e.target.value))}
                                                />
                                            </Col>
                                        </Row>

                                        <button
                                            type='button'
                                            className='btn btn-warning px-4 py-2 fw-bold d-inline-flex align-items-center gap-2'
                                            style={{ background: '#f8be2d', color: '#000', border: 'none' }}
                                            onClick={() => handlePlanSubmit('assign')}
                                            disabled={planLoading}
                                        >
                                            <FaCrown /> {planLoading ? 'Processing...' : 'Activate Plan for User'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 4: FOLLOW & REQUESTS MANAGEMENT */}
                    {activeTab === 'following' && (
                        <div>
                            {/* 1. TOP STAT CARDS */}
                            <Row className='g-3 mb-4'>
                                {[
                                    {
                                        id: 'received',
                                        title: 'Requests Received',
                                        count: followDetails?.counts?.pendingReceived || 0,
                                        icon: <FaInbox size={22} />,
                                        color: '#f8be2d',
                                        desc: 'Pending admin/user approval'
                                    },
                                    {
                                        id: 'sent',
                                        title: 'Requests Sent',
                                        count: followDetails?.counts?.pendingSent || 0,
                                        icon: <FaPaperPlane size={22} />,
                                        color: '#38bdf8',
                                        desc: 'Awaiting recipient approval'
                                    },
                                    {
                                        id: 'followers',
                                        title: 'Followers',
                                        count: followDetails?.counts?.followers || user?.followersCount || 0,
                                        icon: <FaUsers size={22} />,
                                        color: '#22c55e',
                                        desc: 'Active followers'
                                    },
                                    {
                                        id: 'following',
                                        title: 'Following',
                                        count: followDetails?.counts?.following || user?.followingCount || 0,
                                        icon: <FaUserCheck size={22} />,
                                        color: '#a855f7',
                                        desc: 'Accounts followed'
                                    }
                                ].map((stat) => {
                                    const isSelected = followSubTab === stat.id;
                                    return (
                                        <Col key={stat.id} xs={12} sm={6} lg={3}>
                                            <div
                                                onClick={() => setFollowSubTab(stat.id)}
                                                className='p-3 h-100 d-flex flex-column justify-content-between cursor-pointer'
                                                style={{
                                                    background: '#181818',
                                                    borderRadius: '12px',
                                                    border: isSelected ? `2px solid ${stat.color}` : '1px solid #282828',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: isSelected ? `0 4px 18px ${stat.color}25` : 'none'
                                                }}
                                            >
                                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                                    <span className='text-muted' style={{ fontSize: '13px', fontWeight: 600 }}>
                                                        {stat.title}
                                                    </span>
                                                    <div
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '10px',
                                                            background: `${stat.color}15`,
                                                            color: stat.color,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        {stat.icon}
                                                    </div>
                                                </div>
                                                <div className='d-flex align-items-baseline gap-2'>
                                                    <span className='fw-bold text-white fs-3'>{stat.count}</span>
                                                    {stat.id === 'received' && stat.count > 0 && (
                                                        <span className='badge bg-warning text-dark' style={{ fontSize: '10px' }}>
                                                            Needs Action
                                                        </span>
                                                    )}
                                                </div>
                                                <small className='text-muted mt-1' style={{ fontSize: '11px' }}>
                                                    {stat.desc}
                                                </small>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>

                            {/* 2. SUB-TABS & SEARCH BAR */}
                            <div
                                className='p-3 mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3'
                                style={{
                                    background: '#181818',
                                    borderRadius: '12px',
                                    border: '1px solid #282828'
                                }}
                            >
                                {/* Subtab Pills */}
                                <div className='d-flex flex-wrap gap-2'>
                                    {[
                                        { id: 'received', label: 'Received Requests', count: followDetails?.counts?.pendingReceived || 0 },
                                        { id: 'sent', label: 'Sent Requests', count: followDetails?.counts?.pendingSent || 0 },
                                        { id: 'followers', label: 'Followers', count: followDetails?.counts?.followers || 0 },
                                        { id: 'following', label: 'Following', count: followDetails?.counts?.following || 0 }
                                    ].map((st) => {
                                        const isCurrent = followSubTab === st.id;
                                        return (
                                            <button
                                                key={st.id}
                                                type='button'
                                                onClick={() => setFollowSubTab(st.id)}
                                                className='btn btn-sm d-flex align-items-center gap-2 px-3 py-2'
                                                style={{
                                                    background: isCurrent ? '#f8be2d' : '#222',
                                                    color: isCurrent ? '#000' : '#bbb',
                                                    fontWeight: isCurrent ? 700 : 500,
                                                    borderRadius: '8px',
                                                    border: isCurrent ? '1px solid #f8be2d' : '1px solid #333',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <span>{st.label}</span>
                                                <span
                                                    className='badge'
                                                    style={{
                                                        background: isCurrent ? '#000' : '#333',
                                                        color: isCurrent ? '#f8be2d' : '#aaa',
                                                        fontSize: '11px'
                                                    }}
                                                >
                                                    {st.count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Search Input */}
                                <div className='d-flex align-items-center gap-2' style={{ minWidth: '260px' }}>
                                    <div className='input-group input-group-sm'>
                                        <span className='input-group-text' style={{ background: '#222', borderColor: '#333', color: '#888' }}>
                                            <FaSearch />
                                        </span>
                                        <input
                                            type='text'
                                            className='form-control form-control-sm'
                                            placeholder='Search by name, username...'
                                            value={followSearch}
                                            onChange={(e) => setFollowSearch(e.target.value)}
                                            style={{
                                                background: '#222',
                                                borderColor: '#333',
                                                color: '#fff',
                                                fontSize: '13px'
                                            }}
                                        />
                                        {followSearch && (
                                            <button
                                                className='btn btn-sm btn-outline-secondary'
                                                type='button'
                                                onClick={() => setFollowSearch('')}
                                                style={{ borderColor: '#333', color: '#aaa' }}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type='button'
                                        onClick={() => user?._id && fetchFollowDetails(user._id)}
                                        className='btn btn-sm btn-outline-warning'
                                        title='Refresh'
                                    >
                                        ↻
                                    </button>
                                </div>
                            </div>

                            {/* 3. CONTENT LIST */}
                            {followLoading ? (
                                <div className='p-5 text-center text-muted'>
                                    <FaSpinner className='spinner-border spinner-border-sm text-warning mb-2' style={{ width: '2rem', height: '2rem' }} />
                                    <div>Loading follow details...</div>
                                </div>
                            ) : (() => {
                                // Extract list based on current subtab
                                let currentList = [];
                                if (followSubTab === 'received') {
                                    currentList = followDetails.requestsReceived || [];
                                } else if (followSubTab === 'sent') {
                                    currentList = followDetails.requestsSent || [];
                                } else if (followSubTab === 'followers') {
                                    currentList = followDetails.followers || [];
                                } else if (followSubTab === 'following') {
                                    currentList = followDetails.following || [];
                                }

                                // Apply search filter
                                if (followSearch.trim()) {
                                    const q = followSearch.toLowerCase().trim();
                                    currentList = currentList.filter((item) => {
                                        const person = (followSubTab === 'sent' || followSubTab === 'following') ? item.to : item.from;
                                        const nameMatch = person?.name?.toLowerCase().includes(q);
                                        const usernameMatch = person?.username?.toLowerCase().includes(q);
                                        const emailMatch = person?.email?.toLowerCase().includes(q);
                                        return nameMatch || usernameMatch || emailMatch;
                                    });
                                }

                                if (currentList.length === 0) {
                                    return (
                                        <div
                                            className='p-5 text-center'
                                            style={{ background: '#181818', borderRadius: '12px', border: '1px solid #282828' }}
                                        >
                                            <div
                                                style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    borderRadius: '50%',
                                                    background: '#222',
                                                    margin: '0 auto 16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#f8be2d'
                                                }}
                                            >
                                                {followSubTab === 'received' ? <FaInbox size={28} /> :
                                                 followSubTab === 'sent' ? <FaPaperPlane size={28} /> :
                                                 followSubTab === 'followers' ? <FaUsers size={28} /> : <FaUserCheck size={28} />}
                                            </div>
                                            <h6 className='text-white fw-bold mb-1'>
                                                {followSubTab === 'received' ? 'No Pending Follow Requests Received' :
                                                 followSubTab === 'sent' ? 'No Pending Requests Sent' :
                                                 followSubTab === 'followers' ? 'No Followers Found' : 'Not Following Anyone'}
                                            </h6>
                                            <p className='text-muted small mb-0'>
                                                {followSearch
                                                    ? 'No users match your search query.'
                                                    : followSubTab === 'received'
                                                    ? 'This user has no pending incoming follow requests to approve.'
                                                    : 'No records available in this section.'}
                                            </p>
                                        </div>
                                    );
                                }

                                return (
                                    <Row className='g-3'>
                                        {currentList.map((item) => {
                                            const person = (followSubTab === 'sent' || followSubTab === 'following') ? item.to : item.from;
                                            const personImg = person?.profile ? `${Config.AWS_CDN_URL}${person.profile}` : DefultImage;
                                            const isActionLoading = followActionLoading[item._id];

                                            return (
                                                <Col key={item._id} xs={12} md={6} xl={4}>
                                                    <div
                                                        className='p-3 h-100 d-flex flex-column justify-content-between'
                                                        style={{
                                                            background: '#181818',
                                                            borderRadius: '12px',
                                                            border: '1px solid #282828',
                                                            transition: 'transform 0.15s ease, border-color 0.15s ease'
                                                        }}
                                                    >
                                                        {/* User Top Row */}
                                                        <div>
                                                            <div className='d-flex align-items-center gap-3 mb-3'>
                                                                <img
                                                                    src={personImg}
                                                                    alt={person?.username || 'user'}
                                                                    onError={(e) => { e.target.src = DefultImage; }}
                                                                    style={{
                                                                        width: '52px',
                                                                        height: '52px',
                                                                        borderRadius: '50%',
                                                                        objectFit: 'cover',
                                                                        border: '2px solid #333'
                                                                    }}
                                                                />
                                                                <div className='overflow-hidden flex-grow-1'>
                                                                    <div className='d-flex align-items-center gap-2'>
                                                                        <h6 className='text-white fw-bold mb-0 text-truncate'>
                                                                            {person?.name || person?.username || 'User'}
                                                                        </h6>
                                                                        {person?.privacy_status === 'private' ? (
                                                                            <span className='badge bg-secondary' style={{ fontSize: '9px' }}>
                                                                                <MdLock size={10} /> Private
                                                                            </span>
                                                                        ) : (
                                                                            <span className='badge bg-dark text-muted' style={{ fontSize: '9px' }}>
                                                                                <MdPublic size={10} /> Public
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <small className='text-muted d-block text-truncate'>
                                                                        @{person?.username || 'unknown'}
                                                                    </small>
                                                                    {person?.email && (
                                                                        <small className='text-muted d-block text-truncate' style={{ fontSize: '11px' }}>
                                                                            {person.email}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Additional metadata */}
                                                            <div
                                                                className='p-2 mb-3 d-flex align-items-center justify-content-between'
                                                                style={{ background: '#202020', borderRadius: '8px', fontSize: '11px' }}
                                                            >
                                                                <span className='text-muted'>
                                                                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'Recent'}
                                                                </span>
                                                                <span
                                                                    className={`badge ${item.status === 'completed' ? 'bg-success' : 'bg-warning text-dark'}`}
                                                                    style={{ fontSize: '10px' }}
                                                                >
                                                                    {item.status === 'completed' ? 'Connected' : 'Pending Request'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Action Buttons */}
                                                        <div>
                                                            {followSubTab === 'received' && (
                                                                <div className='d-flex gap-2'>
                                                                    {/* MANUAL ACCEPT BUTTON FOR ADMIN */}
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-warning btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2'
                                                                        disabled={!!isActionLoading}
                                                                        onClick={() => handleAcceptFollowRequest(item._id)}
                                                                        style={{
                                                                            fontWeight: 700,
                                                                            borderRadius: '8px',
                                                                            background: '#f8be2d',
                                                                            borderColor: '#f8be2d',
                                                                            color: '#000'
                                                                        }}
                                                                    >
                                                                        {isActionLoading === 'accepting' ? (
                                                                            <>
                                                                                <span className='spinner-border spinner-border-sm' />
                                                                                <span>Accepting...</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FaCheck />
                                                                                <span>Accept</span>
                                                                            </>
                                                                        )}
                                                                    </button>

                                                                    {/* REJECT / DELETE BUTTON */}
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center gap-1 px-3'
                                                                        disabled={!!isActionLoading}
                                                                        onClick={() => handleRejectFollowRequest(item._id)}
                                                                        style={{ borderRadius: '8px' }}
                                                                        title='Reject Request'
                                                                    >
                                                                        {isActionLoading === 'rejecting' ? (
                                                                            <span className='spinner-border spinner-border-sm' />
                                                                        ) : (
                                                                            <>
                                                                                <FaTimes />
                                                                                <span>Reject</span>
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {followSubTab === 'sent' && (
                                                                <div className='d-flex gap-2'>
                                                                    {/* MANUAL ACCEPT BUTTON FOR ADMIN ON SENT REQUEST */}
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-warning btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2'
                                                                        disabled={!!isActionLoading}
                                                                        onClick={() => handleAcceptFollowRequest(item._id)}
                                                                        style={{
                                                                            fontWeight: 700,
                                                                            borderRadius: '8px',
                                                                            background: '#f8be2d',
                                                                            borderColor: '#f8be2d',
                                                                            color: '#000'
                                                                        }}
                                                                        title='Accept this sent request directly'
                                                                    >
                                                                        {isActionLoading === 'accepting' ? (
                                                                            <>
                                                                                <span className='spinner-border spinner-border-sm' />
                                                                                <span>Accepting...</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FaCheck />
                                                                                <span>Accept</span>
                                                                            </>
                                                                        )}
                                                                    </button>

                                                                    {/* CANCEL REQUEST BUTTON */}
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center gap-1 px-3'
                                                                        disabled={!!isActionLoading}
                                                                        onClick={() => handleRejectFollowRequest(item._id)}
                                                                        style={{ borderRadius: '8px', fontSize: '12px' }}
                                                                        title='Cancel Sent Request'
                                                                    >
                                                                        {isActionLoading === 'rejecting' ? (
                                                                            <span className='spinner-border spinner-border-sm' />
                                                                        ) : (
                                                                            <>
                                                                                <FaTimes />
                                                                                <span>Cancel Request</span>
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {(followSubTab === 'followers' || followSubTab === 'following') && (
                                                                <div className='d-flex align-items-center justify-content-between'>
                                                                    <span className='text-success small d-flex align-items-center gap-1' style={{ fontSize: '12px' }}>
                                                                        <FaCheck size={11} /> Active
                                                                    </span>
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-1'
                                                                        disabled={!!isActionLoading}
                                                                        onClick={() => handleRejectFollowRequest(item._id)}
                                                                        style={{
                                                                            borderRadius: '8px',
                                                                            fontSize: '11px',
                                                                            borderColor: '#333',
                                                                            color: '#888'
                                                                        }}
                                                                        title='Remove Connection'
                                                                    >
                                                                        {isActionLoading === 'rejecting' ? (
                                                                            <span className='spinner-border spinner-border-sm' />
                                                                        ) : (
                                                                            'Remove'
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                );
                            })()}
                        </div>
                    )}

                </Container>
            </div>

            {/* MODALS */}
            {/* 1. Edit Event End Date Modal */}
            <EditEventDateModal
                show={showEditDateModal}
                handleClose={() => setShowEditDateModal(false)}
                eventData={selectedEventForEditDate}
                onSuccess={handleEventDateUpdated}
            />

            {/* 2. Event Description Modal */}
            <EventdescView
                show={showDescModal}
                handleClose={() => setShowDescModal(false)}
                EventDesc={selectedEventDesc}
            />

            {/* 3. Event Images Modal */}
            <EventImageView
                show={showImagesModal}
                handleClose={() => setShowImagesModal(false)}
                EventDesc={selectedEventImages}
            />

            {/* 4. Event Address Modal */}
            <AddressView
                show={showAddressModal}
                handleClose={() => setShowAddressModal(false)}
                Address={selectedAddress}
            />
        </div>
    );
}

export default UserDetails;
